import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import API_URL from "../Config";
import axiosInstance from "../axiosinstance";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Table.css";
import "./TicketsV2.css";
import {
  Select,
  MenuItem,
  CircularProgress,
  OutlinedInput,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ClearIcon from "@mui/icons-material/Clear";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import LockIcon from "@mui/icons-material/Lock";
import ReplayIcon from "@mui/icons-material/Replay";
import PaginationFooter from "../Components/PaginationFooter";

const SCOPE_TABS = [
  { key: "all", label: "All" },
  { key: "raised_by_me", label: "Raised by me" },
  { key: "assigned_to_me", label: "Assigned to me" },
];

const STATUS_OPTIONS = [
  "Open",
  "Assigned",
  "In Progress",
  "Resolved",
  "Closed",
  "Reopened",
];
const PRIORITY_OPTIONS = ["Low", "Medium", "High", "Critical"];
const TICKET_TYPE_OPTIONS = ["Alert", "Missing Information"];
const ENTITY_TYPE_OPTIONS = [
  { value: "flange", label: "Flange" },
  { value: "equipment", label: "Equipment" },
];

const PRIORITY_TONES = {
  Low: { bg: "#ecfdf5", color: "#15803d", border: "#bbf7d0", dot: "#22c55e" },
  Medium: { bg: "#fffbeb", color: "#b45309", border: "#fde68a", dot: "#f59e0b" },
  High: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa", dot: "#f97316" },
  Critical: { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca", dot: "#dc2626" },
};

const STATUS_TONES = {
  Open: { bg: "#ecfdf5", color: "#15803d", border: "#bbf7d0", dot: "#22c55e" },
  Assigned: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe", dot: "#3b82f6" },
  "In Progress": { bg: "#f5f3ff", color: "#6d28d9", border: "#ddd6fe", dot: "#7c3aed" },
  Resolved: { bg: "#ecfdf5", color: "#047857", border: "#a7f3d0", dot: "#059669" },
  Closed: { bg: "#f3f4f6", color: "#4b5563", border: "#e5e7eb", dot: "#6b7280" },
  Reopened: { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca", dot: "#dc2626" },
};

const DEFAULT_TONE = { bg: "#f9fafb", color: "#374151", border: "#e5e7eb", dot: "#94a3b8" };

// Each status gets a glyph so it reads at a glance, not just by colour.
const STATUS_ICONS = {
  Open: RadioButtonUncheckedIcon,
  Assigned: AssignmentIndIcon,
  "In Progress": AutorenewIcon,
  Resolved: TaskAltIcon,
  Closed: LockIcon,
  Reopened: ReplayIcon,
};

// Icon + label, tinted with the status colour. Reused by the filter dropdown
// (menu rows and the selected value).
const StatusLabel = ({ status }) => {
  const Icon = STATUS_ICONS[status];
  const tone = STATUS_TONES[status] || DEFAULT_TONE;
  return (
    <span className="tickets-v2-status-option">
      {Icon && <Icon sx={{ fontSize: 16, color: tone.dot || tone.color }} />}
      {status}
    </span>
  );
};

const AVATAR_PALETTE = [
  "#991b1b",
  "#0f766e",
  "#7c3aed",
  "#b45309",
  "#1d4ed8",
  "#be185d",
  "#15803d",
];

const initialsOf = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const colorForName = (name) => {
  const str = String(name || "");
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

const formatRelativeTime = (isoString) => {
  if (!isoString) return "-";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "-";
  const diffMinutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return `${Math.round(diffDays / 30)}mo ago`;
};

const Pill = ({ tone, label, icon: Icon }) => (
  <span
    className="tickets-v2-pill"
    style={{ background: tone.bg, color: tone.color, borderColor: tone.border }}
  >
    {Icon ? (
      <Icon className="tickets-v2-pill-icon" sx={{ fontSize: 14, color: tone.dot || tone.color }} />
    ) : (
      tone.dot && <span className="tickets-v2-pill-dot" style={{ background: tone.dot }} />
    )}
    {label || "-"}
  </span>
);

const AssetCell = ({ ticket }) => {
  if (ticket.entity_type === "flange") {
    return (
      <div>
        <div className="tickets-v2-asset-primary">{ticket.flange_number || "-"}</div>
        {ticket.equipment_number && (
          <div className="tickets-v2-asset-secondary">{ticket.equipment_number}</div>
        )}
      </div>
    );
  }
  return <div className="tickets-v2-asset-primary">{ticket.equipment_number || "-"}</div>;
};

const AssigneesCell = ({ assignees }) => {
  const users = assignees?.users || [];
  const departments = assignees?.departments || [];
  const total = users.length + departments.length;

  if (total === 0) {
    return <span className="tickets-v2-unassigned">Unassigned</span>;
  }

  const shownUsers = users.slice(0, 2);
  const shownDepartments = users.length ? [] : departments.slice(0, 1);
  const overflow = total - shownUsers.length - shownDepartments.length;

  return (
    <div className="tickets-v2-assignees">
      {shownUsers.map((u) => (
        <span
          key={u.id}
          className="tickets-v2-avatar"
          title={u.name || ""}
          style={{ background: colorForName(u.name) }}
        >
          {initialsOf(u.name)}
        </span>
      ))}
      {shownDepartments.map((d) => (
        <span key={d.id} className="tickets-v2-dept-chip">
          {d.name}
        </span>
      ))}
      {overflow > 0 && <span className="tickets-v2-overflow-chip">+{overflow}</span>}
    </div>
  );
};

const clearableFilterInput = (hasValue, onClear) => (
  <OutlinedInput
    endAdornment={
      hasValue ? (
        <InputAdornment position="end">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation(); // prevents dropdown from opening
              onClear();
            }}
          >
            <ClearIcon sx={{ width: 16, height: 16, fontSize: 16, marginRight: "5px" }} />
          </IconButton>
        </InputAdornment>
      ) : null
    }
  />
);

function TicketsV2({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [scope, setScope] = useState("all");

  // Equipment context: when navigated here from a specific equipment, the list
  // is scoped to that equipment's tickets until the user clears the chip.
  const [equipmentId, setEquipmentId] = useState(
    () => searchParams.get("equipment_id") || "",
  );
  const [equipmentLabel, setEquipmentLabel] = useState(
    () => searchParams.get("equipment_label") || "",
  );

  const clearEquipmentFilter = () => {
    setEquipmentId("");
    setEquipmentLabel("");
    const next = new URLSearchParams(searchParams);
    next.delete("equipment_id");
    next.delete("equipment_label");
    setSearchParams(next, { replace: true });
  };

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [ticketType, setTicketType] = useState("");
  const [entityType, setEntityType] = useState("");
  const [projectId, setProjectId] = useState("");
  const [projects, setProjects] = useState([]);

  const [tickets, setTickets] = useState([]);
  const [meta, setMeta] = useState({ page: 1, per_page: 50, total: 0, total_pages: 1 });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);

  // Debounce the search box so we don't hit the API on every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    axiosInstance
      .get(`${API_URL}/api/get_project`)
      .then((res) => setProjects(res.data?.data || []))
      .catch(() => setProjects([]));
  }, []);

  const baseFilterParams = useMemo(
    () => ({
      status: status || undefined,
      priority: priority || undefined,
      ticket_type: ticketType || undefined,
      entity_type: entityType || undefined,
      equipment_id: equipmentId || undefined,
      project_id: projectId || undefined,
      search: search || undefined,
    }),
    [status, priority, ticketType, entityType, equipmentId, projectId, search],
  );

  // Any filter or tab change resets pagination back to page 1.
  useEffect(() => {
    setCurrentPage(1);
  }, [scope, baseFilterParams]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axiosInstance
      .get(`${API_URL}/api/ticket/v2`, {
        params: {
          ...baseFilterParams,
          scope: scope === "all" ? undefined : scope,
          page: currentPage,
          per_page: itemsPerPage,
        },
      })
      .then((res) => {
        if (cancelled) return;
        setTickets(res.data?.data || []);
        setMeta(
          res.data?.meta || { page: 1, per_page: itemsPerPage, total: 0, total_pages: 1 },
        );
      })
      .catch(() => {
        if (cancelled) return;
        setTickets([]);
        toast.error("Failed to load tickets");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [baseFilterParams, scope, currentPage, itemsPerPage]);

  return (
    <div
      className={`table-container tickets-v2-page ${isSidebarExpanded ? "expanded" : "collapsed"}`}
    >
      <ToastContainer containerId="tickets-v2" position="top-right" autoClose={4000} />

      <div className="tickets-v2-page-content">
        {/* Header: title left, search + filters right (same row) */}
        <div className="tickets-v2-page-header">
          <h2 className="page-title mb-0">Tickets</h2>
          <div className="tickets-v2-header-controls-row">
            <div className="tickets-v2-search">
              <SearchIcon className="tickets-v2-search-icon" />
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search ticket #, title, equipment / flange no..."
              />
            </div>
            <Select
              size="small"
              displayEmpty
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              input={clearableFilterInput(!!status, () => setStatus(""))}
              renderValue={(v) => (v ? <StatusLabel status={v} /> : "Status")}
              className="tickets-v2-filter-select"
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  <StatusLabel status={s} />
                </MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              displayEmpty
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              input={clearableFilterInput(!!priority, () => setPriority(""))}
              renderValue={(v) => v || "Priority"}
              className="tickets-v2-filter-select"
              sx={{ minWidth: 110 }}
            >
              <MenuItem value="">All priorities</MenuItem>
              {PRIORITY_OPTIONS.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              displayEmpty
              value={ticketType}
              onChange={(e) => setTicketType(e.target.value)}
              input={clearableFilterInput(!!ticketType, () => setTicketType(""))}
              renderValue={(v) => v || "Type"}
              className="tickets-v2-filter-select"
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">All types</MenuItem>
              {TICKET_TYPE_OPTIONS.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              displayEmpty
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              input={clearableFilterInput(!!entityType, () => setEntityType(""))}
              renderValue={(v) =>
                v ? ENTITY_TYPE_OPTIONS.find((o) => o.value === v)?.label : "Entity"
              }
              className="tickets-v2-filter-select"
              sx={{ minWidth: 110 }}
            >
              <MenuItem value="">All entities</MenuItem>
              {ENTITY_TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              displayEmpty
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              input={clearableFilterInput(!!projectId, () => setProjectId(""))}
              renderValue={(v) =>
                v ? projects.find((p) => p.project_id === v)?.name || "Project" : "Project"
              }
              className="tickets-v2-filter-select"
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">All projects</MenuItem>
              {projects.map((p) => (
                <MenuItem key={p.project_id} value={p.project_id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>

        {/* Scope tabs */}
        <div className="tickets-v2-status-filters">
          {SCOPE_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`tickets-v2-status-filter-chip ${scope === tab.key ? "active" : ""}`}
              onClick={() => setScope(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Equipment context chip (shown when scoped to a single equipment) */}
        {equipmentId && (
          <div className="tickets-v2-context-chip">
            <span>
              Showing tickets for equipment
              {equipmentLabel ? `: ${equipmentLabel}` : ""}
            </span>
            <button
              type="button"
              className="tickets-v2-context-chip-clear"
              onClick={clearEquipmentFilter}
            >
              <ClearIcon fontSize="small" />
              Clear
            </button>
          </div>
        )}

        {/* Table + pagination card */}
        <div className="tickets-v2-table-card">
          <div className="tickets-v2-table-wrapper">
          <table className="tickets-v2-table">
            <thead>
              <tr>
                <th>Ticket #</th>
                <th>Entity </th>
                <th>Title / Project</th>
                <th>Type</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Assignees</th>
                <th>Activity</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="tickets-v2-loading-state">
                    <CircularProgress size={22} />
                  </td>
                </tr>
              ) : tickets.length === 0 ? (
                <tr>
                  <td colSpan={8} className="tickets-v2-empty-state">
                    No tickets found
                  </td>
                </tr>
              ) : (
                tickets.map((ticket) => (
                  <tr
                    key={ticket.id}
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                    style={{ cursor: "pointer" }}
                  >
                    <td className="tickets-v2-title-cell">{ticket.ticket_number}</td>
                    <td>
                      <AssetCell ticket={ticket} />
                    </td>
                    <td style={{ minWidth: 220 }}>
                      <div className="tickets-v2-title-cell">{ticket.title}</div>
                      <div className="tickets-v2-meta-cell">
                        {ticket.project_name || "—"}
                        {ticket.raised_by_name ? ` · by ${ticket.raised_by_name}` : ""}
                      </div>
                    </td>
                    <td>
                      {ticket.ticket_type === "Missing Information"
                        ? "Missing Info"
                        : ticket.ticket_type || "-"}
                    </td>
                    <td>
                      <Pill tone={PRIORITY_TONES[ticket.priority] || DEFAULT_TONE} label={ticket.priority} />
                    </td>
                    <td>
                      <Pill
                        tone={STATUS_TONES[ticket.status] || DEFAULT_TONE}
                        label={ticket.status}
                        icon={STATUS_ICONS[ticket.status]}
                      />
                    </td>
                    <td>
                      <AssigneesCell assignees={ticket.assignees} />
                    </td>
                    <td>
                      <div className="tickets-v2-activity-time">
                        {formatRelativeTime(ticket.last_activity_at)}
                      </div>
                      {ticket.attachment_count > 0 && (
                        <div className="tickets-v2-attachment-count">
                          <AttachFileIcon sx={{ fontSize: 13 }} />
                          {ticket.attachment_count}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>

          <div className="tickets-v2-pagination-footer">
            <PaginationFooter
              totalRecords={meta.total}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              setCurrentPage={setCurrentPage}
              setItemsPerPage={(value) => {
                setItemsPerPage(Math.min(value, 100));
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default TicketsV2;
